import { autoserialize, autoserializeAs } from 'cerialize';

export class Contact {
  @autoserialize name: string;
  @autoserialize email: string;
  @autoserialize phone: string;
  @autoserializeAs('skip_sms') skipSms?: boolean | null;
  @autoserializeAs('skip_email') skipEmail?: boolean | null;

  constructor(contactPayload?: IContact) {
    this.name = contactPayload?.name || '';
    this.email = contactPayload?.email || '';
    this.phone = contactPayload?.phone || '';
    // Left undefined when absent rather than defaulted to false: the API omits the keys
    // entirely on every pre-existing contact, and only a literal `true` silences anything.
    this.skipSms = contactPayload?.skipSms ?? undefined;
    this.skipEmail = contactPayload?.skipEmail ?? undefined;
  }

  get formattedPhoneValue(): string {
    let val: string = this.phone;
    if (val?.length > 0) {
      val = `(${val.slice(0, 3)}) ${val.slice(3, 6)} ${val.slice(6, 10)}`;
    }
    return val;
  }
}

export interface IContact {
  name: string;
  email?: string;
  phone?: string;
  skipSms?: boolean | null;
  skipEmail?: boolean | null;
}

/**
 * RP-4071: whether automated SMS/email to this contact is silenced. The API carries one
 * flag per channel so the two can be split later; product asked for a single control, so
 * the client drives both together and reads the pair as "silenced if either is set".
 */
export function isContactSilenced(contact?: Partial<IContact> | null): boolean {
  return !!contact?.skipSms || !!contact?.skipEmail;
}

/**
 * The patch one toggle applies. `null` for off, never `false`: an all-empty contact is
 * dropped before it is sent (UtilityService.removeNestedObjectWithAllEmptyValues keeps
 * any object holding a non-null value), and a `false` would turn "no contact" into a
 * blank contact object — which attr_json saves over the real one, unvalidated.
 */
export function contactSilencePatch(silenced: boolean): Pick<IContact, 'skipSms' | 'skipEmail'> {
  return silenced ? { skipSms: true, skipEmail: true } : { skipSms: null, skipEmail: null };
}

/**
 * Turn a contact form value carrying the single `silenced` control into the payload the API
 * expects: the contact plus the flag pair, with the control key dropped.
 *
 * The flags ride along ONLY with a contact that holds something. They are non-null, so on
 * an empty contact they would stop UtilityService.removeNestedObjectWithAllEmptyValues from
 * dropping the object, and attr_json writes a brand-new blank Contact over whatever is
 * stored — silently, since Embedded::Contact's name validation is not propagated.
 */
export function contactWithSilence<T extends Partial<IContact> & { silenced?: boolean | null }>(contact: T): Omit<T, 'silenced'> {
  const { silenced, ...rest } = contact;
  const hasValue = !!(rest.name?.trim() || rest.email?.trim() || rest.phone?.trim());
  return hasValue ? { ...rest, ...contactSilencePatch(!!silenced) } : rest;
}

/**
 * RP-4126: the fields a courier contact must carry — a NAME and a PHONE. Email is
 * optional, confirmed with the API team (2026-09-02): dispatch takes only name and phone
 * (`Donations::AssignCourier` → the courier's `name`/`phone` and `dropoff_phone_number`),
 * so an email address is nothing a courier can act on.
 *
 * Exported as ONE rule because two places must apply exactly it: the store form's
 * validators (which courier fields are required while "Accepts Courier" is ticked) and
 * the charity courier tab's ✓/✗ + toggle gate. When they disagreed, the tab could pass a
 * store the form refused to save. RP-4072 redefines what "valid" means — here.
 */
export const COURIER_CONTACT_REQUIRED_FIELDS = ['name', 'phone'] as const satisfies readonly (keyof IContact)[];

/** Whether a courier contact is complete enough to dispatch against. */
export function isCourierContactValid(contact?: Partial<IContact> | null): boolean {
  if (!contact) return false;
  return COURIER_CONTACT_REQUIRED_FIELDS.every((field) => !!contact[field]?.trim());
}

/**
 * RP-4072: is this a dialable US number? The rule third-party couriers (Uber Direct,
 * Roadie) actually enforce, mirrored from the donor webflow's `isValidPhoneNumber` so the
 * two apps agree on one definition — which is what makes `0000000000`, typed to get past
 * a required field, finally fail (the area-code regex rejects it).
 *
 * Lives here, the lowest lib, because both the @core validator and the @rspl-ui phone
 * input need it and `ui` cannot import `core` (core already depends on ui).
 */
export function isValidPhoneNumber(rawInput?: string | null): boolean {
  if (!rawInput) return false;

  const digits = rawInput.replace(/\D/g, '');
  const cleaned = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (cleaned.length !== 10) return false;

  const area = cleaned.slice(0, 3);
  const exchange = cleaned.slice(3, 6);
  const invalidAreaCodes = ['211', '311', '411', '511', '611', '711', '811', '911', '456', '555'];
  const invalidExchangeCodes = ['000', '211', '311', '411', '511', '611', '711', '811', '911', '555'];

  if (!/^[2-9]\d{2}$/.test(area) || invalidAreaCodes.includes(area) || area.endsWith('00')) return false;
  if (!/^[2-9]\d{2}$/.test(exchange) || invalidExchangeCodes.includes(exchange)) return false;

  return true;
}

/**
 * RP-4072: is this an email address the product will accept? PORTED VERBATIM from zod
 * v4.1's own email pattern (`zod/v4/core` → `regexes.email`), which is what
 * `@resupply/types`-side forms in resupply-master enforce through `z.email()`. Copied
 * character for character on purpose: "equivalent" rules drift, an identical one cannot,
 * so the two apps accept exactly the same set by construction rather than by coincidence.
 *
 * Mirrored by hand rather than shared through a package — the same call the phone rule
 * made, because this Angular app is being retired. The sync point is a zod bump in
 * resupply-master; `contact.spec.ts` pins the vectors that would catch a drift.
 *
 * What it means in practice, versus the two rules it replaces here:
 *  - stricter than Angular's `Validators.email`, which accepted six undeliverable shapes:
 *    `info@charity`, `info@localhost`, `a@b`, `info@charity.c`, `info@charity.org2`,
 *    `info@127.0.0.1` (no TLD / one-character or non-alphabetic TLD).
 *  - looser in ONE respect than the hand-rolled pattern it replaces in the donation
 *    screens: an apostrophe in the local part is legal, so `o'brien@charity.org` — a real
 *    name this app used to reject — now passes.
 *
 * Emptiness is deliberately NOT this rule's business: absent is allowed on most contacts,
 * and where it isn't, that is `Validators.required`'s job.
 */
const EMAIL_PATTERN = /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;

export function isValidEmail(rawInput?: string | null): boolean {
  if (!rawInput) return false;
  return EMAIL_PATTERN.test(rawInput);
}
